import { HttpErrorResponse } from '@angular/common/http';
import { OperatorFunction, catchError, repeat, retry, shareReplay, throwError, timer } from 'rxjs';

/**
 * Standard error handler for HTTP requests
 */
export const handleHttpError = (error: HttpErrorResponse) => {
  if (error.status === 0) {
    // A client-side or network error occurred. Handle it accordingly.
    console.error('A client-side error occurred (CORS or network blocking):', error.error);
  } else {
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong.
    console.error(`The server returned code ${error.status}, body was: `, error.error);
  }
  // Return an observable with a user-facing error message.
  return throwError(() => new Error('Something bad happened; please try again later.'));
};

/**
 * Retry with exponential backoff: 1s, 2s, 4s (max 60s)
 *
 * @param maxRetries - Maximum number of retry attempts (default: 2)
 * @param maxDelay - Maximum delay in milliseconds (default: 60000)
 * @returns RxJS operator that retries with exponential backoff
 *
 * @example
 * ```typescript
 * this.http.get('/api/data').pipe(
 *   retryWithBackoff(),
 *   catchError(handleHttpError)
 * )
 * ```
 */
export const retryWithBackoff =
  <T>(maxRetries = 2, maxDelay = 60_000): OperatorFunction<T, T> =>
  (source$) =>
    source$.pipe(
      retry({
        count: maxRetries,
        delay: (error, count) => timer(Math.min(maxDelay, 2 ** count * 1000)),
      }),
    );

/**
 * Poll an HTTP endpoint at the specified interval with retry and caching
 *
 * Combines repeat(), retry with backoff, error handling, and shareReplay
 * for a complete polling solution with resilience and caching.
 *
 * @param intervalMs - Polling interval in milliseconds
 * @returns RxJS operator that polls with retry and caching
 *
 * @example
 * ```typescript
 * this.currentEvents$ = this.http
 *   .get('/api/events')
 *   .pipe(pollAndCache(30_000));
 * ```
 */
export const pollAndCache =
  <T>(intervalMs: number): OperatorFunction<T, T> =>
  (source$) =>
    source$.pipe(
      repeat({ delay: intervalMs }),
      retryWithBackoff(),
      catchError(handleHttpError),
      shareReplay({ bufferSize: 1, refCount: true, windowTime: intervalMs }),
    );
